/**
 * MobileAccountCreate Component
 *
 * Mobile page for creating a new trading account.
 * Route: /settings/portfolio/new
 *
 * Follows MobileBrokerCreate pattern with full AccountFormState fields plus
 * create-only starting balance. Uses 2-stage error handling: hard commit for
 * account creation via AccountsContext, soft post-actions for select/navigate
 * with warning fallback.
 */

// 1. External dependencies
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';

// 2. Workspace packages
import {
  Alert,
  Breadcrumbs,
  BreadcrumbItem,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Checkbox,
  Chip,
  Input,
  NumberInput,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  Switch,
  TextArea,
  useDisclosure,
} from '@enfomentis/ui';

// 3. Internal utilities and constants
import { ACCOUNT_TYPE_OPTIONS } from '@/constants/accountType';
import { CATEGORY_COLORS } from '@/constants/colors';
import { PROP_FIRM_OPTIONS, PROP_FIRM_ASSET_CLASS_OPTIONS, ACCOUNT_STAGE_OPTIONS } from '@/constants/propFirm';
import { CURRENCY_DATABASE } from '@/constants';
import { getCurrencyInfo } from '@/utils';
import type { Account } from '@/types/schema/Account';
import type { AccountType } from '@/types/shared/AccountType';

// 4. Shared components
import { CurrencyChooserModal } from '@/components/shared/CurrencyChooser/CurrencyChooserModal';
import { UnsavedChangesDialog } from '@/components/shared/UnsavedChangesDialog';

// 5. Context
import { useAccounts, normalizeAccountFormState } from '../context';
import type { AccountFormState } from '../context/AccountsContext';

/** Route constants */
const ACCOUNT_LIST_ROUTE = '/settings/portfolio';
const SETTINGS_ROUTE = '/settings';

/** Baseline for dirty tracking */
const INITIAL_FORM_JSON = JSON.stringify(normalizeAccountFormState(null));

export const MobileAccountCreate: React.FC = () => {
  const navigate = useNavigate();
  const unsavedChangesDialog = useDisclosure();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  // Context
  const { createAccount, activeBrokers, brokersLoading, selectAccount, setShowArchived } = useAccounts();

  // Form state (local, not context — create page has no selectedAccount)
  const [formState, setFormState] = useState<AccountFormState>(() => normalizeAccountFormState(null));
  const [startingBalance, setStartingBalance] = useState<number | undefined>(undefined);

  // Validation and submission state
  const [nameError, setNameError] = useState<string | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // UI state
  const [showCurrencyChooser, setShowCurrencyChooser] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [isCustomColor, setIsCustomColor] = useState(false);
  const [customHex, setCustomHex] = useState('');

  // Pending navigation target when showing unsaved changes dialog
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // --- Broker dependency gate ---
  const shouldRedirect = activeBrokers.length === 0 && !brokersLoading;

  useEffect(() => {
    if (shouldRedirect) {
      navigate(ACCOUNT_LIST_ROUTE, { replace: true });
    }
  }, [shouldRedirect, navigate]);

  // --- Derived values ---

  const formDirty = JSON.stringify(formState) !== INITIAL_FORM_JSON;
  const isDirty = formDirty || startingBalance !== undefined;

  // Broker-derived currency
  const selectedBroker = activeBrokers.find((b) => b.id === formState.brokerId);
  const brokerCurrency = selectedBroker?.feeMetadata?.defaultAccountCurrency ?? 'USD';
  const currencyCode = formState.baseCurrency || brokerCurrency;
  const currencyInfo = getCurrencyInfo(currencyCode, CURRENCY_DATABASE);
  const isValidCustomHex = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(customHex.trim());

  // ===========================================================================
  // Form Field Handlers
  // ===========================================================================

  const handleFormFieldChange = useCallback(<K extends keyof AccountFormState>(
    field: K,
    value: AccountFormState[K],
  ): void => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (submitError) setSubmitError(null);
  }, [submitError]);

  const handleNameChange = useCallback(
    (value: string): void => {
      setFormState((prev) => ({ ...prev, name: value }));
      if (nameError) setNameError(null);
      if (submitError) setSubmitError(null);
    },
    [nameError, submitError],
  );

  const handleAccountTypeChange = useCallback((value: AccountType): void => {
    setFormState((prev) => ({
      ...prev,
      accountType: value,
      ...(value !== 'other' ? { customAccountTypeName: '' } : {}),
      ...(value !== 'prop' ? {
        propFirmDetails: {
          firmName: 'apex' as const,
          customFirmName: '',
          assetClass: 'futures' as const,
          customAssetClass: '',
          accountStage: 'evaluation' as const,
        },
      } : {}),
    }));
    if (submitError) setSubmitError(null);
  }, [submitError]);

  const handlePropFirmChange = useCallback(<K extends keyof AccountFormState['propFirmDetails']>(
    field: K,
    value: AccountFormState['propFirmDetails'][K],
  ): void => {
    setFormState((prev) => ({
      ...prev,
      propFirmDetails: { ...prev.propFirmDetails, [field]: value },
    }));
    if (submitError) setSubmitError(null);
  }, [submitError]);

  const handleFeePreferenceChange = useCallback((
    field: keyof AccountFormState['feePreferences'],
    value: boolean,
  ): void => {
    setFormState((prev) => ({
      ...prev,
      feePreferences: { ...prev.feePreferences, [field]: value },
    }));
    if (submitError) setSubmitError(null);
  }, [submitError]);

  const handleColorChange = useCallback((value: string): void => {
    if (value === '__custom__') {
      setIsCustomColor(true);
    } else {
      setIsCustomColor(false);
      setCustomHex('');
      handleFormFieldChange('color', value);
    }
    if (submitError) setSubmitError(null);
  }, [handleFormFieldChange, submitError]);

  const handleCustomHexChange = useCallback((value: string): void => {
    setCustomHex(value);
    const trimmed = value.trim();
    if (/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) {
      const normalized = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
      handleFormFieldChange('color', normalized);
    }
    if (submitError) setSubmitError(null);
  }, [handleFormFieldChange, submitError]);

  const handleCurrencySelect = useCallback((code: string): void => {
    handleFormFieldChange('baseCurrency', code);
    setShowCurrencyChooser(false);
  }, [handleFormFieldChange]);

  const handleBalanceChange = useCallback((value: number | undefined): void => {
    setStartingBalance(value);
    if (balanceError) setBalanceError(null);
    if (submitError) setSubmitError(null);
  }, [balanceError, submitError]);

  const addTag = useCallback((value: string): void => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const normalized = trimmed.toLowerCase();
    setFormState((prev) => {
      if (prev.tags.some((t) => t.toLowerCase() === normalized)) return prev;
      return { ...prev, tags: [...prev.tags, trimmed] };
    });
    setTagInput('');
    if (submitError) setSubmitError(null);
  }, [submitError]);

  const removeTag = useCallback((tag: string): void => {
    setFormState((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
    if (submitError) setSubmitError(null);
  }, [submitError]);

  const handleTagKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
  }, [addTag, tagInput]);

  // ===========================================================================
  // Navigation Handlers
  // ===========================================================================

  const handleNavigateWithDirtyCheck = useCallback(
    (target: string): void => {
      if (isSubmittingRef.current) return;
      if (isDirty) {
        setPendingNavigation(target);
        unsavedChangesDialog.onOpen();
      } else {
        navigate(target);
      }
    },
    [isDirty, navigate, unsavedChangesDialog],
  );

  const handleBack = useCallback((): void => {
    handleNavigateWithDirtyCheck(ACCOUNT_LIST_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  const handleNavigateToSettings = useCallback((): void => {
    handleNavigateWithDirtyCheck(SETTINGS_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  const handleCancel = useCallback((): void => {
    handleNavigateWithDirtyCheck(ACCOUNT_LIST_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  const handleDiscard = useCallback((): void => {
    unsavedChangesDialog.onClose();
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [unsavedChangesDialog, navigate, pendingNavigation]);

  // ===========================================================================
  // Validation
  // ===========================================================================

  const validateForm = useCallback((): boolean => {
    let isValid = true;

    const trimmedName = formState.name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      nameInputRef.current?.focus();
      isValid = false;
    } else {
      setNameError(null);
    }

    if (!formState.brokerId) {
      setSubmitError('Please select a broker');
      isValid = false;
    }

    if (startingBalance !== undefined && startingBalance < 0) {
      setBalanceError('Starting balance must be 0 or greater');
      isValid = false;
    } else {
      setBalanceError(null);
    }

    return isValid;
  }, [formState.name, formState.brokerId, startingBalance]);

  // ===========================================================================
  // Create Handler (2-stage error handling)
  // ===========================================================================

  const handleCreate = useCallback(async (): Promise<void> => {
    // Double-tap guard (ref-lock — synchronous, immune to same-frame races)
    if (isSubmittingRef.current) return;

    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    // Lock acquired only after validation passes (no deadlock on validation failure)
    isSubmittingRef.current = true;

    const trimmedName = formState.name.trim();
    let createdAccountId: string;

    // Build Partial<Account> with exactOptionalPropertyTypes compliance
    const data: Partial<Account> = {
      name: trimmedName,
      brokerId: formState.brokerId,
      accountType: formState.accountType,
      ...(formState.description.trim() ? { description: formState.description.trim() } : {}),
      ...(formState.accountType === 'other' && formState.customAccountTypeName.trim()
        ? { customAccountTypeName: formState.customAccountTypeName.trim() }
        : {}),
      ...(formState.accountType === 'prop'
        ? {
            propFirmDetails: {
              firmName: formState.propFirmDetails.firmName,
              assetClass: formState.propFirmDetails.assetClass,
              accountStage: formState.propFirmDetails.accountStage,
              ...(formState.propFirmDetails.firmName === 'other' && formState.propFirmDetails.customFirmName.trim()
                ? { customFirmName: formState.propFirmDetails.customFirmName.trim() }
                : {}),
              ...(formState.propFirmDetails.assetClass === 'other' && formState.propFirmDetails.customAssetClass.trim()
                ? { customAssetClass: formState.propFirmDetails.customAssetClass.trim() }
                : {}),
            },
          }
        : {}),
      ...(formState.accountNumber.trim() ? { accountNumber: formState.accountNumber.trim() } : {}),
      ...(formState.baseCurrency ? { baseCurrency: formState.baseCurrency } : {}),
      ...(Object.values(formState.feePreferences).some((v) => v)
        ? { feePreferences: formState.feePreferences }
        : {}),
      ...(formState.color ? { color: formState.color } : {}),
      ...(formState.tags.length > 0 ? { tags: formState.tags } : {}),
    };

    // Stage 1: Hard commit — create account (failure = user retries)
    try {
      setIsSubmitting(true);
      const newAccount = await createAccount(data, startingBalance);
      createdAccountId = newAccount.id;
    } catch (error) {
      // Context already logs and shows danger toast — set inline alert for persistent feedback
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create account';
      setSubmitError(errorMessage);
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      return;
    }

    // Stage 2: Soft post-actions — select/navigate (failure = warning, still navigate)
    try {
      setShowArchived(false);
      selectAccount(createdAccountId);
    } catch (error) {
      console.warn('Post-create actions failed:', error);
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      navigate(`${ACCOUNT_LIST_ROUTE}/${createdAccountId}`);
    }
  }, [
    formState,
    startingBalance,
    validateForm,
    createAccount,
    selectAccount,
    setShowArchived,
    navigate,
  ]);

  // ===========================================================================
  // Render
  // ===========================================================================

  // Don't render while redirecting due to broker dependency gate
  if (shouldRedirect) {
    return null;
  }

  return (
    <>
      <Card radius="none" shadow="none" className="h-full flex flex-col">
        {/* Header — Breadcrumb Navigation + Page Title */}
        <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
          <div className="flex items-center">
            <Breadcrumbs variant="light" size="sm">
              <BreadcrumbItem
                onPress={handleNavigateToSettings}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                Settings
              </BreadcrumbItem>
              <BreadcrumbItem
                onPress={handleBack}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                Portfolio
              </BreadcrumbItem>
            </Breadcrumbs>
          </div>
          <h1 className="text-lg font-semibold text-text-primary mt-1">New Account</h1>
        </CardHeader>

        {/* Content */}
        <CardBody className="flex-1 overflow-y-auto px-1 pb-2">
          <Card>
            <CardBody className="space-y-4">
              {/* --- Core Details --- */}

              {/* Name (required) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Name <span className="text-danger">*</span>
                </label>
                <Input
                  ref={nameInputRef}
                  placeholder="Enter account name"
                  value={formState.name}
                  onValueChange={handleNameChange}
                  isInvalid={!!nameError}
                  errorMessage={nameError}
                  autoFocus
                  className="[&_input]:text-base"
                />
              </div>

              {/* Account Type */}
              <Select
                label="Account Type"
                labelPlacement="outside"
                selectedKeys={[formState.accountType]}
                onSelectionChange={(keys) => {
                  if (keys === 'all') return;
                  const val = Array.from(keys)[0] as AccountType | undefined;
                  if (val) handleAccountTypeChange(val);
                }}
                isRequired
              >
                {ACCOUNT_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} textValue={opt.label}>
                    {opt.label}
                  </SelectItem>
                ))}
              </Select>

              {/* Custom Type Name (conditional) */}
              {formState.accountType === 'other' && (
                <Input
                  label="Custom Type Name"
                  labelPlacement="outside"
                  placeholder="Enter custom account type..."
                  value={formState.customAccountTypeName}
                  onValueChange={(val) => handleFormFieldChange('customAccountTypeName', val)}
                  className="[&_input]:text-base"
                />
              )}

              {/* Account Number */}
              <Input
                label="Account Number"
                labelPlacement="outside"
                placeholder="Optional account number"
                value={formState.accountNumber}
                onValueChange={(val) => handleFormFieldChange('accountNumber', val)}
                className="[&_input]:text-base"
              />

              {/* Description */}
              <TextArea
                label="Description"
                labelPlacement="outside-top"
                placeholder="Optional description or notes..."
                value={formState.description}
                onValueChange={(val) => handleFormFieldChange('description', val)}
                minRows={2}
                maxRows={4}
                className="[&_textarea]:text-base"
              />

              {/* Starting Balance (create-only) */}
              <NumberInput
                label="Starting Balance"
                placeholder="0.00"
                value={startingBalance}
                onValueChange={handleBalanceChange}
                minValue={0}
                isInvalid={!!balanceError}
                errorMessage={balanceError}
                formatOptions={{
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }}
              />

              {/* --- Prop Firm Details (conditional) --- */}
              {formState.accountType === 'prop' && (
                <>
                  <div className="border-t border-default-200" />
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-text-primary">Prop Firm Details</p>

                    <Select
                      label="Firm Name"
                      labelPlacement="outside"
                      selectedKeys={[formState.propFirmDetails.firmName]}
                      onSelectionChange={(keys) => {
                        if (keys === 'all') return;
                        const val = Array.from(keys)[0] as typeof formState.propFirmDetails.firmName | undefined;
                        if (val) {
                          handlePropFirmChange('firmName', val);
                          if (val !== 'other') handlePropFirmChange('customFirmName', '');
                        }
                      }}
                    >
                      {PROP_FIRM_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} textValue={opt.label}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </Select>

                    {formState.propFirmDetails.firmName === 'other' && (
                      <Input
                        label="Custom Firm Name"
                        labelPlacement="outside"
                        placeholder="Enter prop firm name..."
                        value={formState.propFirmDetails.customFirmName}
                        onValueChange={(val) => handlePropFirmChange('customFirmName', val)}
                        className="[&_input]:text-base"
                      />
                    )}

                    <Select
                      label="Asset Class"
                      labelPlacement="outside"
                      selectedKeys={[formState.propFirmDetails.assetClass]}
                      onSelectionChange={(keys) => {
                        if (keys === 'all') return;
                        const val = Array.from(keys)[0] as typeof formState.propFirmDetails.assetClass | undefined;
                        if (val) {
                          handlePropFirmChange('assetClass', val);
                          if (val !== 'other') handlePropFirmChange('customAssetClass', '');
                        }
                      }}
                    >
                      {PROP_FIRM_ASSET_CLASS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} textValue={opt.label}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </Select>

                    {formState.propFirmDetails.assetClass === 'other' && (
                      <Input
                        label="Custom Asset Class"
                        labelPlacement="outside"
                        placeholder="Enter asset class..."
                        value={formState.propFirmDetails.customAssetClass}
                        onValueChange={(val) => handlePropFirmChange('customAssetClass', val)}
                        className="[&_input]:text-base"
                      />
                    )}

                    <Select
                      label="Account Stage"
                      labelPlacement="outside"
                      selectedKeys={[formState.propFirmDetails.accountStage]}
                      onSelectionChange={(keys) => {
                        if (keys === 'all') return;
                        const val = Array.from(keys)[0] as typeof formState.propFirmDetails.accountStage | undefined;
                        if (val) handlePropFirmChange('accountStage', val);
                      }}
                    >
                      {ACCOUNT_STAGE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} textValue={opt.label}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </>
              )}

              {/* --- Broker & Currency --- */}
              <div className="border-t border-default-200" />
              <div className="space-y-4">
                <p className="text-sm font-semibold text-text-primary">Broker & Currency</p>

                {/* Broker Select */}
                <Select
                  label="Broker"
                  labelPlacement="outside"
                  selectedKeys={formState.brokerId ? [formState.brokerId] : []}
                  onSelectionChange={(keys) => {
                    if (keys === 'all') return;
                    const val = Array.from(keys)[0] as string | undefined;
                    if (val) handleFormFieldChange('brokerId', val);
                  }}
                  isRequired
                >
                  {activeBrokers.map((broker) => (
                    <SelectItem key={broker.id} textValue={broker.name}>
                      {broker.name}
                    </SelectItem>
                  ))}
                </Select>

                {/* Broker currency info */}
                {selectedBroker && (
                  <p className="text-xs text-text-tertiary -mt-2">
                    {selectedBroker.name} &middot; Default: {brokerCurrency}
                  </p>
                )}

                {/* Currency Override */}
                <div className="min-w-0 space-y-2">
                  <Switch
                    size="sm"
                    isSelected={formState.baseCurrency !== ''}
                    isDisabled={!formState.brokerId}
                    onValueChange={(on) => {
                      if (on) {
                        handleFormFieldChange('baseCurrency', brokerCurrency);
                      } else {
                        handleFormFieldChange('baseCurrency', '');
                      }
                    }}
                  >
                    Override broker default currency
                  </Switch>
                  {formState.baseCurrency ? (
                    <Button
                      variant="bordered"
                      className="w-full justify-between text-base font-normal border-default-200"
                      endContent={<Globe className="w-4 h-4 text-text-tertiary shrink-0" />}
                      onPress={() => setShowCurrencyChooser(true)}
                      isDisabled={isSubmitting}
                    >
                      {currencyInfo ? (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{currencyInfo.symbol}</span>
                          <span>{currencyInfo.name}</span>
                          <span className="text-text-secondary">({currencyCode})</span>
                        </div>
                      ) : (
                        <span>{currencyCode}</span>
                      )}
                    </Button>
                  ) : (
                    <p className="text-xs text-text-tertiary">
                      {formState.brokerId
                        ? `Using broker default: ${brokerCurrency}${currencyInfo ? ` — ${currencyInfo.name}` : ''}`
                        : 'Select a broker first'}
                    </p>
                  )}
                </div>
              </div>

              {/* --- Fee Preferences --- */}
              <div className="border-t border-default-200" />
              <div className="space-y-3">
                <p className="text-sm font-semibold text-text-primary">Fee Preferences</p>
                <Checkbox
                  isSelected={formState.feePreferences.includeFeeInPnL}
                  onValueChange={(val) => handleFeePreferenceChange('includeFeeInPnL', val)}
                >
                  Include fees in P&amp;L calculations
                </Checkbox>
                <Checkbox
                  isSelected={formState.feePreferences.promptFeeVerification}
                  onValueChange={(val) => handleFeePreferenceChange('promptFeeVerification', val)}
                >
                  Prompt fee verification on new instruments
                </Checkbox>
                <Checkbox
                  isSelected={formState.feePreferences.trackOvernightFinancing}
                  onValueChange={(val) => handleFeePreferenceChange('trackOvernightFinancing', val)}
                >
                  Track overnight financing costs
                </Checkbox>
                <Checkbox
                  isSelected={formState.feePreferences.trackCurrencyConversion}
                  onValueChange={(val) => handleFeePreferenceChange('trackCurrencyConversion', val)}
                >
                  Track currency conversion costs
                </Checkbox>
              </div>

              {/* --- Organization --- */}
              <div className="border-t border-default-200" />
              <div className="space-y-4">
                <p className="text-sm font-semibold text-text-primary">Organization</p>

                {/* Color Picker */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-secondary">Color</label>
                  <RadioGroup
                    value={isCustomColor ? '__custom__' : formState.color}
                    onValueChange={handleColorChange}
                    orientation="horizontal"
                    classNames={{ wrapper: 'gap-3 flex-wrap' }}
                  >
                    {CATEGORY_COLORS.map((color) => (
                      <Radio
                        key={color.value}
                        value={color.value}
                        classNames={{ base: 'm-0' }}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded-full border-2 border-default-300"
                            style={{ backgroundColor: color.value }}
                          />
                          <span className="text-sm">{color.name}</span>
                        </div>
                      </Radio>
                    ))}
                    <Radio
                      value="__custom__"
                      classNames={{ base: 'm-0' }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-full border-2 border-default-300"
                          style={{
                            backgroundColor: isCustomColor && isValidCustomHex
                              ? (customHex.trim().startsWith('#') ? customHex.trim() : `#${customHex.trim()}`)
                              : '#cccccc',
                          }}
                        />
                        <span className="text-sm">Custom</span>
                      </div>
                    </Radio>
                  </RadioGroup>
                  {isCustomColor && (
                    <div className="flex items-center gap-3 mt-2">
                      <Input
                        label="Hex Color"
                        placeholder="#ff5500"
                        value={customHex}
                        onValueChange={handleCustomHexChange}
                        variant="bordered"
                        size="sm"
                        className="max-w-40"
                        isInvalid={customHex.length > 0 && !isValidCustomHex}
                        errorMessage={
                          customHex.length > 0 && !isValidCustomHex
                            ? 'Invalid hex color'
                            : undefined
                        }
                      />
                      {isValidCustomHex && (
                        <div
                          className="w-8 h-8 rounded-full border-2 border-default-300 shrink-0"
                          style={{ backgroundColor: customHex.trim().startsWith('#') ? customHex.trim() : `#${customHex.trim()}` }}
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-secondary">Tags</label>
                  <Input
                    placeholder="Add tag and press Enter..."
                    value={tagInput}
                    onValueChange={setTagInput}
                    onKeyDown={handleTagKeyDown}
                    size="sm"
                    className="[&_input]:text-base"
                  />
                  {formState.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {formState.tags.map((tag) => (
                        <Chip
                          key={tag}
                          size="sm"
                          variant="flat"
                          onClose={() => removeTag(tag)}
                        >
                          {tag}
                        </Chip>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Error Alert */}
              {submitError && (
                <Alert color="danger" variant="flat">
                  {submitError}
                </Alert>
              )}
            </CardBody>
          </Card>
        </CardBody>

        {/* Footer */}
        <CardFooter className="flex items-center justify-end gap-1 pt-2 border-t border-default-200">
          <Button
            variant="flat"
            size="sm"
            onPress={handleCancel}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            size="sm"
            onPress={handleCreate}
            isLoading={isSubmitting}
          >
            Create
          </Button>
        </CardFooter>
      </Card>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={unsavedChangesDialog.isOpen}
        onClose={unsavedChangesDialog.onClose}
        onDiscard={handleDiscard}
      />

      {/* Currency Chooser Modal */}
      <CurrencyChooserModal
        isOpen={showCurrencyChooser}
        onClose={() => setShowCurrencyChooser(false)}
        selectedCurrency={formState.baseCurrency || brokerCurrency}
        onCurrencySelect={handleCurrencySelect}
        title="Account Currency"
      />
    </>
  );
};
